import { defineStore } from 'pinia';
import axios from 'axios';

export const useWalletsStore = defineStore('wallets', {
    state: () => ({
        items: [],
        deposits: [],
        withdrawals: [],
        fiatDeposits: [],
        fiatWithdrawals: [],
        notifications: [],
        futureWallets: [],
        transferHistory: [],
        // Enhanced wallet state
        portfolioStats: null,
        recentTransactions: [],
        favorites: {
            currency: [],
            market: [],
        },
        preferences: {},
        securityStatus: null,
        lastUpdated: null,
        isLoading: false,
        error: null,
        // Server time for notification "time ago" (avoids timezone/clock skew)
        serverTime: null,
        clientTimeAtFetch: null,
        /** Set to true after first fetch so layout does not refetch on every page change */
        notificationsFetched: false,
    }),

    getters: {
        getWallets: (state) => state.items,
        getNotificationsFetched: (state) => state.notificationsFetched,
        
        getWallet: (state) => (symbol) => {
            return state.items.find((wallet) => wallet.symbol === symbol);
        },
        
        getNotifications: (state) => state.notifications,
        
        getDeposits: (state) => state.deposits,
        
        getFiatDeposits: (state) => state.fiatDeposits,
        
        getWithdrawals: (state) => state.withdrawals,
        
        getFiatWithdrawals: (state) => state.fiatWithdrawals,
        
        getFutureWallets: (state) => state.futureWallets,

        getTransferHistory: (state) => state.transferHistory,

        // Enhanced getters
        getPortfolioStats: (state) => state.portfolioStats,
        
        getRecentTransactions: (state) => state.recentTransactions,
        
        getSecurityStatus: (state) => state.securityStatus,
        
        getFavorites: (state) => state.favorites,
        
        getFavoriteCurrencies: (state) => state.favorites.currency,
        
        isCurrencyFavorite: (state) => (currencyId) => {
            return state.favorites.currency.includes(currencyId);
        },
        
        getPreferences: (state) => state.preferences,
        
        getPreference: (state) => (key, defaultValue = null) => {
            return state.preferences[key] ?? defaultValue;
        },
        
        getLastUpdated: (state) => state.lastUpdated,
        
        getIsLoading: (state) => state.isLoading,

        // Computed wallet stats
        getFavoriteWallets: (state) => {
            return state.items.filter(wallet => wallet.is_favorite);
        },
        
        getWalletsWithBalance: (state) => {
            return state.items.filter(wallet => parseFloat(wallet.balance_total_usdt) > 0);
        },
    },

    actions: {
        async fetchWallets(route) {
            try {
                const response = await axios.get(route);
                this.items = response.data.data;
                this.lastUpdated = new Date().toISOString();
            } catch (error) {
                console.error('Failed to fetch wallets:', error);
            }
        },

        async fetchEnhancedWallets(route) {
            this.isLoading = true;
            this.error = null;
            try {
                const response = await axios.get(route);
                this.items = response.data.data;
                this.lastUpdated = new Date().toISOString();
            } catch (error) {
                console.error('Failed to fetch enhanced wallets:', error);
                this.error = error.message || 'Failed to fetch wallets';
            } finally {
                this.isLoading = false;
            }
        },

        async fetchPortfolioStats(route) {
            try {
                const response = await axios.get(route);
                // Support both { total_value_usdt, ... } and { data: { total_value_usdt, ... } }
                this.portfolioStats = response.data?.data ?? response.data ?? null;
            } catch (error) {
                console.error('Failed to fetch portfolio stats:', error);
            }
        },

        async fetchRecentTransactions(route) {
            try {
                const response = await axios.get(route);
                this.recentTransactions = response.data.data || [];
            } catch (error) {
                console.error('Failed to fetch recent transactions:', error);
            }
        },

        async fetchSecurityStatus(route) {
            try {
                const response = await axios.get(route);
                this.securityStatus = response.data;
            } catch (error) {
                console.error('Failed to fetch security status:', error);
            }
        },

        async fetchFavorites(route) {
            try {
                const response = await axios.get(route);
                if (response.data.favorites) {
                    this.favorites = response.data.favorites;
                }
            } catch (error) {
                console.error('Failed to fetch favorites:', error);
            }
        },

        async toggleFavorite(type, id, route) {
            try {
                const response = await axios.post(route, { type, id });
                if (response.data.success) {
                    // Update local state
                    if (response.data.is_favorite) {
                        if (!this.favorites[type].includes(id)) {
                            this.favorites[type].push(id);
                        }
                    } else {
                        const index = this.favorites[type].indexOf(id);
                        if (index > -1) {
                            this.favorites[type].splice(index, 1);
                        }
                    }
                    // Update wallet if it's a currency favorite
                    if (type === 'currency') {
                        // Update spot wallet
                        const wallet = this.items.find(w => w.currency_id === id);
                        if (wallet) {
                            wallet.is_favorite = response.data.is_favorite;
                        }
                        // Update futures wallet
                        const futureWallet = this.futureWallets.find(w => w.currency_id === id);
                        if (futureWallet) {
                            futureWallet.is_favorite = response.data.is_favorite;
                        }
                    }
                    return response.data.is_favorite;
                }
            } catch (error) {
                console.error('Failed to toggle favorite:', error);
                throw error;
            }
        },

        async fetchPreferences(route) {
            try {
                const response = await axios.get(route);
                if (response.data.preferences) {
                    this.preferences = response.data.preferences;
                }
            } catch (error) {
                console.error('Failed to fetch preferences:', error);
            }
        },

        async setPreference(key, value, route) {
            try {
                await axios.post(route, { key, value });
                this.preferences[key] = value;
            } catch (error) {
                console.error('Failed to set preference:', error);
                throw error;
            }
        },

        async refresh(routes) {
            this.isLoading = true;
            this.error = null;
            
            try {
                const promises = [];
                
                if (routes.wallets) {
                    promises.push(this.fetchEnhancedWallets(routes.wallets));
                }
                if (routes.portfolioStats) {
                    promises.push(this.fetchPortfolioStats(routes.portfolioStats));
                }
                if (routes.recentTransactions) {
                    promises.push(this.fetchRecentTransactions(routes.recentTransactions));
                }
                if (routes.securityStatus) {
                    promises.push(this.fetchSecurityStatus(routes.securityStatus));
                }
                if (routes.favorites) {
                    promises.push(this.fetchFavorites(routes.favorites));
                }
                
                await Promise.all(promises);
                this.lastUpdated = new Date().toISOString();
            } catch (error) {
                console.error('Failed to refresh wallet data:', error);
                this.error = error.message || 'Failed to refresh data';
            } finally {
                this.isLoading = false;
            }
        },

        // Save hide balance preference to localStorage as backup
        setHideBalance(value) {
            try {
                localStorage.setItem('wallet.hide_balance', value ? '1' : '0');
            } catch (e) {
                // localStorage not available
            }
        },

        getHideBalanceFromStorage() {
            try {
                const value = localStorage.getItem('wallet.hide_balance');
                return value === '1';
            } catch (e) {
                return false;
            }
        },

        async fetchNotification(route) {
            if (this.notificationsFetched) return;
            try {
                const response = await axios.get(route);
                this.notifications = response.data.notifications ?? [];
                this.notificationsFetched = true;
                if (response.data.server_time) {
                    this.serverTime = response.data.server_time;
                    this.clientTimeAtFetch = Date.now();
                }
            } catch (error) {
                console.error('Failed to fetch notifications:', error);
            }
        },
        /**
         * Current "now" in server time for relative timestamps (notification "time ago").
         * Uses server_time from last notifications fetch to avoid timezone/clock skew.
         */
        getServerNow() {
            if (this.serverTime && this.clientTimeAtFetch != null) {
                const serverNowMs = new Date(this.serverTime).getTime() + (Date.now() - this.clientTimeAtFetch);
                return new Date(serverNowMs);
            }
            return new Date();
        },

        async fetchDeposits(route) {
            try {
                const response = await axios.get(route);
                this.deposits = response.data.data;
            } catch (error) {
                console.error('Failed to fetch deposits:', error);
            }
        },

        async fetchWithdrawals(route) {
            try {
                const response = await axios.get(route);
                this.withdrawals = response.data.data;
            } catch (error) {
                console.error('Failed to fetch withdrawals:', error);
            }
        },

        async fetchFiatDeposits(route) {
            try {
                const response = await axios.get(route);
                this.fiatDeposits = response.data.data;
            } catch (error) {
                console.error('Failed to fetch fiat deposits:', error);
            }
        },

        async fetchFiatWithdrawals(route) {
            try {
                const response = await axios.get(route);
                this.fiatWithdrawals = response.data.data;
            } catch (error) {
                console.error('Failed to fetch fiat withdrawals:', error);
            }
        },

        updateWallet(payload) {
            const w = payload.wallet;
            if (!w) return;

            const index =
                w.id != null
                    ? this.items.findIndex((item) => item.id === w.id)
                    : this.items.findIndex((item) => item.symbol === w.symbol);

            if (index > -1) {
                const prev = this.items[index];
                // Merge so realtime payloads (narrower shape) never strip API fields such as currency_id.
                this.items[index] = { ...prev, ...w };
            } else if (w.id != null) {
                // First balance a user ever holds in this coin: the wallet was not in the list the
                // layout fetched on load, so append it instead of dropping the update.
                this.items.push(w);
            }
        },

        storeDeposit(payload) {
            this.deposits.unshift(payload.deposit);
        },

        storeNotification(payload) {
            const notification = payload?.notification;
            if (!notification) return;
            // Guard against duplicate broadcast deliveries: the same .NotifyUpdated
            // broadcast can reach the client more than once. Each notification has a
            // stable DB id, so drop any we already hold instead of prepending an
            // identical row to the panel (the cause of the doubled bell entries).
            if (notification.id != null && this.notifications.some((n) => n && n.id === notification.id)) {
                return;
            }
            this.notifications.unshift(notification);
        },

        updateDeposit(payload) {
            const index = this.deposits.findIndex(item => item.id === payload.deposit.id);
            if (index > -1) {
                this.deposits[index] = payload.deposit;
            }
        },

        storeWithdrawal(payload) {
            this.withdrawals.unshift(payload.withdrawal);
        },

        updateWithdrawal(payload) {
            const index = this.withdrawals.findIndex(item => item.id === payload.withdrawal.id);
            if (index > -1) {
                this.withdrawals[index] = payload.withdrawal;
            }
        },

        async fetchFutureWallets(route) {
            try {
                const response = await axios.get(route);
                const account = response.data?.data || response.data;
                this.futureWallets = account?.id ? [account] : [];
            } catch (error) {
                console.error('Failed to fetch future wallets:', error);
            }
        },

        async fetchTransferHistory(route, limit = 10) {
            try {
                const response = await axios.get(route, { params: { limit } });
                this.transferHistory = response.data.data || [];
            } catch (error) {
                console.error('Failed to fetch transfer history:', error);
            }
        },

        updateFutureWallet(payload) {
            const fw = payload.futureWallet || payload.futureAccount;
            if (!fw) return;
            // Prop futures accounts (challenge/verification demo + funded real)
            // broadcast `.FuturesAccountUpdated` on the same `user-{id}` channel
            // as the user's regular futures wallet. They must never be merged
            // into — or pushed onto — the regular futures wallet list that the
            // layout's Futures tab renders, otherwise a prop balance shows up
            // as a phantom second USDT row (and inflates the futures total)
            // until the user reloads. Skip anything tagged as a prop account.
            if (fw.account_kind === 'prop' || Number(fw.prop_account_id || 0) > 0) {
                return;
            }
            const index = this.futureWallets.findIndex(item => item.id === fw.id);
            if (index > -1) {
                this.futureWallets[index] = fw;
            } else {
                this.futureWallets.push(fw);
            }
        },

        async transferToFutures(route, payload) {
            try {
                const response = await axios.post(route, payload);
                if (response.data?.spot_wallet) {
                    this.updateWallet({ wallet: response.data.spot_wallet });
                }
                if (response.data?.future_account) {
                    this.updateFutureWallet({ futureAccount: response.data.future_account });
                }
                return response.data;
            } catch (error) {
                console.error('Failed to transfer to futures:', error);
                throw error;
            }
        },

        async transferFromFutures(route, payload) {
            try {
                const response = await axios.post(route, payload);
                if (response.data?.spot_wallet) {
                    this.updateWallet({ wallet: response.data.spot_wallet });
                }
                if (response.data?.future_account) {
                    this.updateFutureWallet({ futureAccount: response.data.future_account });
                }
                return response.data;
            } catch (error) {
                console.error('Failed to transfer from futures:', error);
                throw error;
            }
        }
    }
});



